import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Profile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profileUser, setProfileUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [games, setGames] = useState([]);
  const [history, setHistory] = useState({ games: [], totals: {}, pagination: {} });
  const [selectedGame, setSelectedGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observerRef = useRef();
  const loadMoreRef = useRef();

  const isOwnProfile = currentUser?.id === parseInt(userId);

  useEffect(() => {
    fetchProfileData();
    fetchGames();
  }, [userId]);

  useEffect(() => {
    fetchHistory(true);
  }, [userId, selectedGame]);

  const fetchProfileData = async () => {
    try {
      const statsRes = await fetch(`${window.API_URL}/api/scores/stats/user/${userId}`);
      const statsData = await statsRes.json();
      setStats(statsData);
      setProfileUser(statsData);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchGames = async () => {
    try {
      const res = await fetch(`${window.API_URL}/api/games`);
      const data = await res.json();
      setGames(data);
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  };

  const fetchHistory = async (reset = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const offset = reset ? 0 : history.games.length;
      const gameParam = selectedGame ? `&gameId=${selectedGame.id}` : '';
      const res = await fetch(
        `${window.API_URL}/api/scores/history/user/${userId}?limit=15&offset=${offset}${gameParam}`
      );
      const data = await res.json();

      if (reset) {
        setHistory(data);
      } else {
        setHistory(prev => ({
          ...data,
          games: [...prev.games, ...data.games]
        }));
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Infinite scroll observer
  const handleObserver = useCallback((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting && history.pagination?.hasMore && !loadingMore) {
      fetchHistory(false);
    }
  }, [history.pagination?.hasMore, loadingMore]);

  useEffect(() => {
    const option = { root: null, rootMargin: '20px', threshold: 0 };
    observerRef.current = new IntersectionObserver(handleObserver, option);
    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleObserver]);

  const formatTimeAgo = (date) => {
    const now = new Date();
    const then = new Date(date);
    const diff = Math.floor((now - then) / 1000);

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return then.toLocaleDateString();
  };

  const getPlacementBadge = (placement) => {
    if (placement === 1) return '🥇';
    if (placement === 2) return '🥈';
    if (placement === 3) return '🥉';
    return `#${placement}`;
  };

  const winRate = stats?.games_played > 0
    ? Math.round((parseInt(stats?.total_wins || 0) / parseInt(stats?.games_played || 1)) * 100)
    : 0;

  const heroStats = [
    { label: 'Games Played', value: stats?.games_played || 0, icon: '🎮' },
    { label: 'Total Wins', value: stats?.total_wins || 0, icon: '🏆' },
    { label: 'Win Rate', value: `${winRate}%`, icon: '📈' },
    { label: 'Avg Score', value: stats?.avg_score || '—', icon: '📊' },
    { label: 'Best Score', value: history.totals?.best_score || '—', icon: '⭐' },
    { label: 'Achievements', value: stats?.earned_milestones?.length || 0, icon: '🎯' },
  ];

  if (!stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <p className="text-white">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/')}
            className="text-slate-400 hover:text-white"
          >
            ← Back
          </button>
          <div className="flex-1" />
          {isOwnProfile && (
            <button
              onClick={() => navigate('/achievements')}
              className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm"
            >
              View Achievements
            </button>
          )}
        </div>

        {/* Profile Header Card */}
        <div className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center font-bold text-3xl text-white shadow-lg"
              style={{ backgroundColor: stats.avatar_color || '#6366f1' }}
            >
              {stats.name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {stats.name}
                {isOwnProfile && <span className="text-indigo-400 text-base ml-2">(you)</span>}
              </h1>
              <p className="text-slate-400">Player Profile</p>
            </div>
          </div>

          {/* Hero Stats - 2 rows of 3 */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {heroStats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-xl mb-1">{stat.icon}</div>
                <div className="text-xl font-bold text-white">{stat.value}</div>
                <div className="text-xs text-slate-400 uppercase">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Per-Game Stats */}
        {stats.game_stats && stats.game_stats.length > 0 && (
          <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
            <h2 className="text-sm font-semibold text-white mb-3">Game Breakdown</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {stats.game_stats.map((gameStat, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg"
                >
                  <span className="text-2xl">{gameStat.emoji}</span>
                  <div className="flex-1">
                    <div className="text-white font-medium">{gameStat.game_name}</div>
                    <div className="text-slate-400 text-sm">
                      {gameStat.games_played} games • {gameStat.wins} wins • Best: {gameStat.best_score}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-green-400 font-bold">{gameStat.wins}</div>
                    <div className="text-slate-500 text-xs">wins</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Earned Achievements */}
        {stats.earned_milestones && stats.earned_milestones.length > 0 && (
          <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-sm font-semibold text-white">Recent Achievements</h2>
              <button
                onClick={() => navigate('/achievements')}
                className="text-xs text-blue-400 hover:text-blue-300"
              >
                View All →
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {stats.earned_milestones.slice(0, 8).map((milestone, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-3 py-2 bg-amber-500/20 border border-amber-500/30 rounded-lg"
                >
                  <span className="text-xl">{milestone.emoji}</span>
                  <div>
                    <div className="text-white text-sm font-medium">{milestone.name}</div>
                    <div className="text-amber-400 text-xs">{formatTimeAgo(milestone.earned_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Game Filter Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          <button
            onClick={() => setSelectedGame(null)}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              !selectedGame
                ? 'bg-white text-slate-900'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            All Games
          </button>
          {games.map(game => (
            <button
              key={game.id}
              onClick={() => setSelectedGame(game)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                selectedGame?.id === game.id
                  ? 'bg-white text-slate-900'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <span>{game.emoji}</span>
              <span>{game.name}</span>
            </button>
          ))}
        </div>

        {/* Game History Table */}
        <div className="bg-slate-800/50 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-white/10 flex justify-between items-center">
            <h2 className="font-semibold text-white">Game History</h2>
            <div className="text-slate-400 text-sm">
              {history.totals?.total_entries || 0} games
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading...</div>
          ) : history.games.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              No games played yet.
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {history.games.map((game, index) => (
                <div
                  key={index}
                  className="p-4 hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-3xl">{game.emoji}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{game.game_name}</span>
                        <span className={`text-lg ${game.placement === 1 ? 'text-yellow-400' : ''}`}>
                          {getPlacementBadge(game.placement)}
                        </span>
                      </div>
                      <div className="text-slate-400 text-sm">
                        Score: <span className="text-white">{game.score}</span>
                        {' • '}
                        {game.player_count} players
                        {' • '}
                        {formatTimeAgo(game.played_at)}
                      </div>
                      {/* Other players */}
                      <div className="flex gap-1 mt-2">
                        {game.all_players?.slice(0, 5).map((p, i) => (
                          <div
                            key={i}
                            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                              p.user_id === parseInt(userId) ? 'ring-2 ring-blue-500' : ''
                            }`}
                            style={{ backgroundColor: p.avatar_color || '#6366f1' }}
                            title={`${p.name}: ${p.score} pts (#${p.placement})`}
                          >
                            {p.name?.charAt(0)}
                          </div>
                        ))}
                        {(game.all_players?.length || 0) > 5 && (
                          <div className="w-6 h-6 rounded-full bg-slate-600 flex items-center justify-center text-xs">
                            +{game.all_players.length - 5}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-xl font-bold ${game.placement === 1 ? 'text-green-400' : 'text-white'}`}>
                        {game.score}
                      </div>
                      <div className="text-slate-500 text-xs">points</div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Load More Indicator */}
              <div ref={loadMoreRef} className="p-4 text-center">
                {loadingMore && <span className="text-slate-400">Loading more...</span>}
                {!loadingMore && history.pagination?.hasMore && (
                  <span className="text-slate-500 text-sm">Scroll for more</span>
                )}
                {!history.pagination?.hasMore && history.games.length > 0 && (
                  <span className="text-slate-500 text-sm">End of history</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
